"use client";

import { Label } from "@/src/components/shadcn/ui/label";
import { RadioGroup, RadioGroupItem } from "@/src/components/shadcn/ui/radio-group";
import type { OnlineSchedulingExistingAppointment } from "@/src/lib/scheduling/online-scheduling-schemas";
import { formatSlotDateTime } from "@/src/lib/scheduling/slot-display";

export function RescheduleVisitStep({
  appointments,
  selected,
  onSelect,
}: {
  appointments: OnlineSchedulingExistingAppointment[];
  selected: OnlineSchedulingExistingAppointment | null;
  onSelect: (appointment: OnlineSchedulingExistingAppointment) => void;
}) {
  if (appointments.length === 0) {
    return (
      <p role="status">
        You have no upcoming appointments eligible for online rescheduling. Go back to patient details to schedule a new
        appointment.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Select the appointment you would like to reschedule. Your provider and appointment type will stay the same.
      </p>
      <RadioGroup
        aria-label="Appointment to reschedule"
        value={selected?.id ?? ""}
        onValueChange={(id) => {
          const appointment = appointments.find((item) => item.id === id);

          if (appointment) onSelect(appointment);
        }}
      >
        {appointments.map((appointment) => (
          <div
            key={appointment.id}
            className="flex items-start gap-3 rounded-lg border border-border bg-background p-4"
          >
            <RadioGroupItem id={`appointment-${appointment.id}`} value={appointment.id} className="mt-1" />
            <Label htmlFor={`appointment-${appointment.id}`} className="space-y-1 font-normal">
              <span className="block font-medium">{formatSlotDateTime(appointment.start)}</span>
              <span className="block">{appointment.appointmentTypeName}</span>
              {appointment.providerDisplayName && <span className="block">{appointment.providerDisplayName}</span>}
              {appointment.locationName && (
                <span className="block text-muted-foreground">{appointment.locationName}</span>
              )}
            </Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
}
